import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';
import {
  Grid,
  Container,
  Header,
  Image,
  Message,
  Form,
  Popup,
  List,
  Button,
  Icon,
} from 'semantic-ui-react';
import * as yup from 'yup';

import {useHttpClient} from 'hooks';
import httpClient from 'httpClient';
import {addSuccessMessage} from 'state/actions';

import UserSearch from '../AuthSearch/UserSearch';
import GroupSearchField from '../Forms/GroupSearchField';
import ProjectInformationBanner, {
  BANNER_TYPES,
} from '../ProjectInformationBanner/ProjectInformationBanner';
import RemoveProjectModal from '../RemoveProjectModal';
import {
  categoryOptions,
  categoryPurposes,
  sitesTypeInfo,
  validationSchemas,
  wordpressCategoryOptions,
  wordpressCategoryPurposes,
  isWordPress,
} from '../sharedElements';
import {typeLabel} from '../siteTypesLabels';

import styles from './GeneralInfo.module.scss';

const defaultFormValues = {
  name: '',
  displayName: '',
  createdOn: '',
  category: '',
  description: '',
  type: '',
  owner: '',
  adminGroup: '',
  applicationPortalLink: '',
};

const formSchema = yup.object().shape({
  category: validationSchemas.categoryValidation,
  description: validationSchemas.descriptionValidation,
  owner: yup.string().required('The site needs an owner'),
});

const GeneralInfo = ({
  projectName,
  projectType,
  removeProjectMessage,
  onCategoryChange,
  onOwnerInfoChange,
  setProject,
}) => {
  const dispatch = useDispatch();
  const {
    data,
    error,
    loading: isLoadingProjectData,
    cancel,
    refetch,
  } = useHttpClient({
    url: `/api/${projectType}/project/${projectName}`,
    method: 'GET',
  });

  const {
    control,
    handleSubmit,
    formState: {isSubmitting, isDirty, dirtyFields, isSubmitSuccessful, isValid},
    reset,
    getValues,
    trigger,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formSchema),
  });

  const [blockedInfo, setBlockedInfo] = useState(null);
  const [readOnlyReason, setReadOnlyReason] = useState('');

  const [categoryValue, adminGroupValue] = watch(['category', 'adminGroup']);

  // Determine if editing should be disabled
  // Priority: blocked > read-only
  const isEditingDisabled = Boolean(blockedInfo) || Boolean(readOnlyReason);

  useEffect(() => {
    if (data && !isLoadingProjectData && !error) {
      const {
        project: {
          owner: {username: owner, type},
          ...projectData
        },
      } = data;

      reset({
        ...projectData,
        type: projectData.type,
        adminGroup: projectData.adminGroup === null ? '' : projectData.adminGroup,
        application_portal_link:
          projectData.applicationPortalLink === null ? '' : projectData.application_portal_link,
        owner,
      });

      if (onCategoryChange) {
        onCategoryChange(projectData.category);
      }

      if (onOwnerInfoChange) {
        onOwnerInfoChange({username: owner, type: type});
      }

      if (setProject) {
        setProject(projectData);
      }

      setBlockedInfo(projectData.blocked);
      setReadOnlyReason(projectData.readOnlyReason || '');
    }
  }, [data, error, isLoadingProjectData, reset, onCategoryChange, onOwnerInfoChange, setProject]);

  useEffect(() => {
    if (error) {
      // If there's an error loading project data, show in read-only mode
      setReadOnlyReason(
        'There was a problem while fetching details of the site. Data is shown in read-only mode.'
      );
    }
  }, [error]);

  useEffect(() => {
    if (data && !isLoadingProjectData && !error) {
      trigger();
    }
  }, [data, isLoadingProjectData, error, trigger]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      refetch();
    }
  }, [isSubmitSuccessful, refetch]);

  useEffect(() => {
    return () => {
      cancel('Request cancelled since GeneralInfo component has been unmounted.');
    };
  }, [cancel]);

  const submitHandler = async data => {
    if (setProject) {
      setProject(undefined);
    }

    await httpClient.patch(`/api/${projectType}/project/${projectName}`, {
      category: dirtyFields.category ? data.category : undefined,
      description: dirtyFields.description ? data.description : undefined,
      owner: dirtyFields.owner ? data.owner : undefined,
      adminGroup: dirtyFields.adminGroup
        ? data.adminGroup === ''
          ? null
          : data.adminGroup
        : undefined,
    });

    dispatch(
      addSuccessMessage(
        `The site ${projectName} was updated. It can take a few seconds until your changes are fully synchronized.`
      )
    );

    if (dirtyFields.owner) {
      dispatch(
        addSuccessMessage(
          `Please note that the new owner needs to accept the transfer ownership request now. Until that the ownership of the site will stay the same.`
        )
      );
    }

    if (setProject) {
      setProject(data);
    }
  };

  if (error) {
    return <Message error>There was a problem while fetching details of the site.</Message>;
  }

  return (
    <Container className={styles.GeneralInfo} textAlign="center">
      {/* Blocked banner takes precedence over read-only */}
      {blockedInfo && (
        <ProjectInformationBanner
          type={BANNER_TYPES.BLOCKED}
          projectName={projectName}
          metadata={blockedInfo}
        />
      )}

      {/* Only show read-only banner if NOT blocked */}
      {!blockedInfo && readOnlyReason && (
        <ProjectInformationBanner
          type={BANNER_TYPES.READ_ONLY}
          projectName={projectName}
          message={readOnlyReason}
        />
      )}

      {(blockedInfo || readOnlyReason) && <div style={{marginTop: '2em'}}></div>}

      <Form onSubmit={handleSubmit(submitHandler)} loading={isLoadingProjectData || isSubmitting}>
        <Header as="h3">
          <Image
            src={sitesTypeInfo[projectType]['icon']}
            alt={`${projectType} icon`}
            className={styles.HeaderIcon}
          />{' '}
          Configuration of <i>{getValues().displayName || getValues().name}</i>
        </Header>
        <Grid columns={2} stackable>
          <Grid.Row>
            <Grid.Column className={styles.LeftColumn}>
              <Header as="h4" textAlign="center" dividing>
                General
              </Header>
              <Grid.Row>
                <Grid verticalAlign="middle">
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <Popup
                        content="The name of the site"
                        position="left center"
                        trigger={<label>Name</label>}
                      />
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      {getValues().displayName || getValues().name}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Row>
              <Grid.Row>
                <Grid verticalAlign="middle">
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <Popup
                        content="The date when the site was created"
                        position="left center"
                        trigger={<label>Created on</label>}
                      />
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      {getValues().createdOn
                        ? new Date(getValues().createdOn).toLocaleString()
                        : ''}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Row>
              {(projectType === 'app-catalogue' || projectType === 'nexus') && (
                <Grid.Row>
                  <Grid verticalAlign="middle">
                    <Grid.Row columns={2}>
                      <Grid.Column textAlign="right" width={4}>
                        <Popup
                          trigger={<label htmlFor="type">Type</label>}
                          content="The type of the site"
                          position="left center"
                        />
                      </Grid.Column>
                      <Grid.Column textAlign="left" width={12}>
                        {typeLabel[getValues().resourceType] || getValues().resourceType}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Row>
              )}
              <Grid.Row>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          trigger={<label htmlFor="category">Category*</label>}
                          position="left center"
                          content={
                            <List
                              items={
                                isWordPress(projectType)
                                  ? wordpressCategoryPurposes
                                  : categoryPurposes
                              }
                            />
                          }
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      <Controller
                        control={control}
                        name="category"
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                          <Form.Select
                            id="category"
                            name={name}
                            value={value}
                            error={error?.message}
                            options={
                              isWordPress(projectType) ? wordpressCategoryOptions : categoryOptions
                            }
                            onChange={(_evt, data) => {
                              onChange(data.value);
                              if (onCategoryChange) {
                                onCategoryChange(data.value);
                              }
                            }}
                            disabled={isEditingDisabled}
                          />
                        )}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Row>
              <Grid.Row>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          trigger={<label htmlFor="description">Description*</label>}
                          content="The description of the site's purpose"
                          position="left center"
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      <Controller
                        control={control}
                        name="description"
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                          <Form.TextArea
                            id="description"
                            name={name}
                            value={value}
                            error={error?.message}
                            onChange={(_evt, data) => onChange(data.value)}
                            rows={1}
                            disabled={isEditingDisabled}
                          />
                        )}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Row>
            </Grid.Column>
            <Grid.Column className={styles.RightColumn}>
              <Header as="h4" textAlign="center" dividing>
                Website management
              </Header>
              <Grid.Row>
                <Grid>
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          content="Site owner has the permissions to manage the site. Change the owner to transfer ownership to another person (must be a CERN primary or service account). You have to use the login name."
                          trigger={<label htmlFor="owner">Owner*</label>}
                          position="left center"
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      <Controller
                        control={control}
                        name="owner"
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                          <UserSearch
                            id="owner"
                            name={name}
                            value={value}
                            error={error?.message}
                            onChange={data => {
                              onChange(data.upn);
                              if (onOwnerInfoChange) {
                                onOwnerInfoChange({username: data.upn, type: data.type});
                              }
                            }}
                            disabled={isEditingDisabled}
                          />
                        )}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Row>
              <Grid.Row>
                <Grid verticalAlign="middle">
                  <Grid.Row columns={2}>
                    <Grid.Column textAlign="right" width={4}>
                      <div className={styles.FixedLabel}>
                        <Popup
                          content="A group with site management permissions identical to those of site owner. Only CERN primary or service accounts group members are considered administrators."
                          trigger={<label htmlFor="adminGroup">Administrator group</label>}
                          position="left center"
                        />
                      </div>
                    </Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      <Controller
                        control={control}
                        name="adminGroup"
                        render={({field: {onChange, value, name}}) => (
                          <GroupSearchField
                            id="adminGroup"
                            name={name}
                            value={value}
                            onChange={data => {
                              onChange(data.groupIdentifier ? data.groupIdentifier : '');
                            }}
                            showRecommendation={categoryValue === 'Official' && !adminGroupValue}
                            recommendationMessage="It is strongly recommended to set administrator group for Official sites."
                            disablePopup
                            disabled={isEditingDisabled}
                          />
                        )}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  {projectType === 'wordpress' && (
                    <Grid.Row columns={2} style={{paddingTop: 0}}>
                      <Grid.Column textAlign="right" width={4}></Grid.Column>
                      <Grid.Column textAlign="left" width={12}>
                        <a
                          href="https://wordpress.docs.cern.ch/wordpress/roles-and-permissions/managing-access"
                          target="_blank"
                          rel="noreferrer"
                        >
                          How to link WordPress roles to groups{' '}
                          <Icon name="external alternate" size="small" />
                        </a>
                      </Grid.Column>
                    </Grid.Row>
                  )}
                  <Grid.Row columns={2} style={{paddingTop: 0}}>
                    <Grid.Column textAlign="right" width={4}></Grid.Column>
                    <Grid.Column textAlign="left" width={12}>
                      {adminGroupValue ? (
                        <a
                          href={`https://groups-portal.web.cern.ch/group/${adminGroupValue}/details`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Add users to the admin group ({adminGroupValue}) via the Groups Portal{' '}
                          <Icon name="external alternate" size="small" />
                        </a>
                      ) : (
                        <a
                          href="https://groups-portal.web.cern.ch"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Create admin group via the Groups Portal{' '}
                          <Icon name="external alternate" size="small" />
                        </a>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                  {getValues().applicationPortalLink !== '' && (
                    <Grid.Row columns={2} style={{paddingTop: 0}}>
                      <Grid.Column textAlign="right" width={4}></Grid.Column>
                      <Grid.Column textAlign="left" width={12}>
                        <a
                          href={getValues().applicationPortalLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Manage roles via the Application Portal{' '}
                          <Icon name="external alternate" size="small" />
                        </a>
                      </Grid.Column>
                    </Grid.Row>
                  )}
                  {projectType !== 'WebEOS' && (
                    <Grid.Row columns={2} style={{paddingTop: 0}}>
                      <Grid.Column textAlign="right" width={4}></Grid.Column>
                      <Grid.Column textAlign="left" width={12}>
                        <a
                          href={sitesTypeInfo[projectType]['documentationUrl']}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {sitesTypeInfo[projectType]['info']}{' '}
                          <Icon name="external alternate" size="small" />
                        </a>
                      </Grid.Column>
                    </Grid.Row>
                  )}
                </Grid>
              </Grid.Row>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column textAlign="right">
              <Button
                className={styles.SaveButton}
                type="submit"
                disabled={isLoadingProjectData || !isDirty || !isValid || isEditingDisabled}
              >
                Save
              </Button>
              <RemoveProjectModal
                trigger={
                  <Button
                    type="button"
                    className={styles.DeleteButton}
                    disabled={isEditingDisabled}
                  >
                    Delete
                  </Button>
                }
                projectName={projectName}
                content={removeProjectMessage}
                onConfirm={async () => {
                  await httpClient.delete(`/api/${projectType}/project/${projectName}`);
                }}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </Container>
  );
};

GeneralInfo.propTypes = {
  projectName: PropTypes.string.isRequired,
  projectType: PropTypes.string.isRequired,
  removeProjectMessage: PropTypes.node.isRequired,
  onCategoryChange: PropTypes.func,
  onOwnerInfoChange: PropTypes.func,
  setProject: PropTypes.func,
};

export default GeneralInfo;
